import React, {useEffect, useMemo, useState} from "react";
import {getProfile} from "../../firebase";
import { useParams, useSearchParams } from "react-router-dom";
import {useMacoSpectrum} from "../../hooks/useMacoSpectrum";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/pro-light-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {SpectrumPlot} from "../../components/SpectrumPlot";
import {filterHIPeaks} from "../../utils/filter-hi-peaks";
import {getMatches} from "../../utils/get-matches";
import {getShiftedSpectra} from "../../utils/get-shifted-spectra";
import {getEmptyZones} from "../../utils/get-empty-zones";

export const MacoMatchDetail: React.FC = () => {
    const { spectra, isLoading: isSpectraLoadig, error: linkError } = useMacoSpectrum();
    const [profile, setProfile] = useState<{ mw: number; subunit: string, species_plus: string }[] | undefined>(undefined);
    const [isProfileLoading, setIsProfileLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(
        undefined
    );
    const [ppm, setPpm] = useState<number>(800);
    const [database, setDatabase] = useState<string | undefined>(undefined);
    const [numberHIPeaks, setNumberHIPeaks] = useState<number>(50);
    const [getHIPeaks, setGetHIPeaks] = useState<boolean>(false);
    const [shift, setShift] = useState<number>(0);
    const [min, setMin] = useState<number>(3000);
    const [max, setMax] = useState<number>(20000);
    const [useCalibratedMasses, setUseCalibratedMasses] = useState<boolean>(false);
    const { selectedProfile } = useParams();
    const [searchParams] = useSearchParams();

    const hasCalibratedMasses = spectra&& spectra.calibratedMasses && spectra.calibratedMasses.length > 0;

    // on first render set database, ppm from search params
    useEffect(() => {
        if (searchParams.has("database")) {
            setDatabase(searchParams.get("database") as string);
        }

        if (searchParams.has("ppm")) {
            setPpm(parseInt(searchParams.get("ppm") as string));
        }

        if (searchParams.has("hi")) {
            setNumberHIPeaks(parseInt(searchParams.get("hi") as string));
        }

        if (searchParams.has("getHI")) {
            setGetHIPeaks(searchParams.get("getHI") === "true");
        }

        if (searchParams.has("shift")) {
            setShift(parseInt(searchParams.get("shift") as string));
        }

        if (searchParams.has("min")) {
            setMin(parseInt(searchParams.get("min") as string));
        }

        if (searchParams.has("max")) {
            setMax(parseInt(searchParams.get("max") as string));
        }

        if (searchParams.has("useCalibratedMasses")) {
            setUseCalibratedMasses(searchParams.get("useCalibratedMasses") === "true");
        }
    }, []);

    // load profile
    useEffect(() => {
        if (selectedProfile && selectedProfile.length === 0) return;
        if (selectedProfile === undefined) return;
        if(database === undefined) return;

        setIsProfileLoading(true);
        getProfile({ profile: selectedProfile, database: database }).then(({ data }) => {
            setProfile(data);
            setIsProfileLoading(false);
        }).catch((error) => {
            setErrorMessage(error.message);
            setIsProfileLoading(false);
        })
    }, [database]);

    if (isSpectraLoadig) {
        return (
            <div className="container min-vh-100 align-items-center">
                <div
                    className="position-absolute top-50 start-50 spinner-border text-primary"
                    role="status"
                >
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (spectra === undefined) {
        return (
            <div className="container">
                <div className="row">
                    <div className="col-12">
                        <FontAwesomeIcon icon={faExclamationTriangle as IconProp} />{" "}
                        {linkError ?? "Spectra with not found."}
                    </div>
                </div>
            </div>
        );
    }

    let masses = Array.isArray(spectra.masses) ? spectra.masses: [];

    if(hasCalibratedMasses && useCalibratedMasses){
        masses = Array.isArray(spectra.calibratedMasses) ? spectra.calibratedMasses.map(x => ({mz: x.mz, int: x.intensity})) : [];
    }
    masses = masses.filter((mass) => mass.mz >= min && mass.mz <= max);
    if(getHIPeaks) masses = filterHIPeaks(masses, numberHIPeaks);

    if(shift !== 0) masses = getShiftedSpectra(shift, masses);

    const emptyZones = getEmptyZones(masses);

    const filteredProfiles = profile?.filter(x => x.mw >= min && x.mw <= max);

    // filter empty zones from profile
    const profileWithoutEmptyZones = filteredProfiles?.filter((entry) => {
        if (!entry.mw) return false;
        return !emptyZones.some(zone => entry.mw >= zone.start && entry.mw <= zone.end);
    })

    const matchData = getMatches(masses || [], ppm, profile || []);
    // count mz not null in matchDetailData
    const matches: number = matchData.filter((entry) => !!entry.mz).length;

    const species = profile && profile?.length > 0 ? profile[0].species_plus : "";

    return (
        <div className="container">
            <div className="row">
                <div className="col-12 col-lg-6 mb-10 mb-lg-10">
                    <div>
            <span>
              Filename: <strong>{spectra?.file}</strong>
            </span>
                    </div>
                    <div>
            <span>
              Sample: <strong>{spectra?.sample}</strong>
            </span>
                    </div>
                    <div>
            <span>
              Datacount:{" "}
                <strong>{spectra?.masses && spectra.masses.length}</strong>
            </span>
                    </div>
                    <div>
            <span>
              Matches: {isProfileLoading ? (
                <span className="spinner-border spinner-border-sm text-primary" role="status"><span className="visually-hidden">Loading...</span></span>
              ) : (
                <strong>{matches > 0 ? matches : ""} / {filteredProfiles?.length} ({profileWithoutEmptyZones?.length})</strong>
              )}
            </span>
                    </div>
                    <div>
            <span>
              Species: {isProfileLoading ? (
                <span className="spinner-border spinner-border-sm text-primary" role="status"><span className="visually-hidden">Loading...</span></span>
              ) : (
                <strong>{species}</strong>
              )}
            </span>
                    </div>
                <div>
            <span>
              Matched Profile: <strong>{selectedProfile}</strong>
            </span>
                </div>
            </div>

            <form onSubmit={async (e) => {
                e.preventDefault()
            }}>
                <div className="col-12">
                    <div className="row mb-20">
                        <div className="col-4">
                            <div className="input-group">
                                <span className="input-group-text">PPM</span>
                                <input
                                    type="number"
                                    className="form-control"
                                    min={0}
                                    value={ppm}
                                    step={50}
                                    onChange={(e) => setPpm(parseInt(e.target.value))}
                                />
                            </div>
                        </div>
                        <div className="col-4">
                            <div className="input-group">
                                <span className="input-group-text">HI</span>
                                <input
                                    type="number"
                                    className="form-control"
                                    min={0}
                                    value={numberHIPeaks}
                                    step={10}
                                    onChange={(e) => setNumberHIPeaks(parseInt(e.target.value))}
                                />
                                <div className="input-group-text">
                                    <input
                                        type="checkbox"
                                        checked={getHIPeaks}
                                        onChange={(e) => setGetHIPeaks(e.target.checked)}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-2">
                            <div className="input-group">
                                <span className="input-group-text">Calibrated</span>
                                <div className="input-group-text">
                                    <input
                                        disabled={!hasCalibratedMasses}
                                        type="checkbox"
                                        checked={useCalibratedMasses}
                                        onChange={(e) => setUseCalibratedMasses(e.target.checked)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row mb-20">
                        <div className="col-2">
                            <div className="input-group">
                                <span className="input-group-text">Shift</span>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={shift}
                                    step={100}
                                    onChange={(e) => setShift(parseInt(e.target.value))}
                                />
                            </div>
                        </div>
                        <div className="col-3">
                            <div className="input-group">
                                <span className="input-group-text">Min</span>
                                <input
                                    type="number"
                                    className="form-control"
                                    min={3000}
                                    max={max}
                                    value={min}
                                    step={500}
                                    onChange={(e) => setMin(parseInt(e.target.value))}
                                />
                            </div>
                        </div>
                        <div className="col-3">
                            <div className="input-group">
                                <span className="input-group-text">Max</span>
                                <input
                                    type="number"
                                    className="form-control"
                                    min={min}
                                    max={20000}
                                    value={max}
                                    step={500}
                                    onChange={(e) => setMax(parseInt(e.target.value))}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-12 mb-20">
                    <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={window.close}>
                        Close
                    </button>
                </div>
            </form>


                {errorMessage && (<div className="col-12 text-center mt-10"><FontAwesomeIcon
                    icon={faExclamationTriangle as IconProp}/> {errorMessage}</div>)}


                {masses && masses.length > 0 && (
                    <SpectrumPlot
                        masses={masses}
                        min={min}
                        max={max}
                        matchedProfile={isProfileLoading ? [] : matchData.map(({mw, subunit, mz, subunitIntensity, mz_error}) => ({
                            mw,
                            subunit,
                            matched: !!mz,
                            intensity: subunitIntensity,
                            mz_error
                        }))}
                    />
                )}
            </div>
        </div>
    );
};
